import { useCallback, useEffect, useState } from 'react';
import type { BadgeDraft, BadgeView, RewardConfig, RewardSnapshot, RewardsExplainerView } from '@contracts';
import { useDomainApi, useDomainVersion } from '@ui/shared/domain/DomainApiContext';
import { SkeletonBlock } from '@ui/shared/primitives/Skeleton';
import { Trophy } from '@ui/shared/icons';
import { BadgeDetailSheet } from '@ui/desktop/rewards/BadgeDetailSheet';
import { BadgeEditorSheet } from '@ui/desktop/rewards/BadgeEditorSheet';
import { ThemeSwitcher } from '@ui/desktop/rewards/ThemeSwitcher';
import { XPSourceRow } from './XPSourceRow';
import { LevelArcRow } from './LevelArcRow';
import { BadgeListRow } from './BadgeListRow';

/**
 * `RewardsScreenPhone` (settings-rewards.md v2.1 §11) — the phone-adapted
 * "How rewards work" tree. Fixes the reported bug: the desktop 3-column
 * XP-sources table rendered unadapted at ~390px (a single row's "when it
 * pays" sentence wrapped 7+ lines). This restructures every section into
 * the stacked title/subtitle/value pattern `today-view.md` §1.3 already
 * established (`RuledCheckRow`), reusing established phone primitives
 * rather than a narrowed table.
 *
 * Data/domain logic is intentionally IDENTICAL to `RewardsScreenDesktop` —
 * only the render tree differs (responsive-strategy.md §2.2's "only the
 * render tree forks" rule). Shared, cross-cutting pieces (BadgeDetailSheet,
 * BadgeEditorSheet, ThemeSwitcher, IconPickerPopover via those sheets) are
 * reused unchanged from the desktop tree per §11.9's component inventory —
 * they were never table/grid-shaped to begin with.
 */
export function RewardsScreenPhone() {
  const api = useDomainApi();
  const version = useDomainVersion();

  const [snapshot, setSnapshot] = useState<RewardSnapshot | null>(null);
  const [explainer, setExplainer] = useState<RewardsExplainerView | null>(null);
  const [config, setConfig] = useState<RewardConfig | null>(null);
  const [badges, setBadges] = useState<readonly BadgeView[] | null>(null);

  const [editing, setEditing] = useState(false);
  const [draftConfig, setDraftConfig] = useState<RewardConfig | null>(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  const [selectedBadge, setSelectedBadge] = useState<BadgeView | null>(null);
  const [editingBadge, setEditingBadge] = useState<BadgeView | null>(null);
  const [creatingBadge, setCreatingBadge] = useState(false);

  const load = useCallback(async () => {
    const [snap, exp, cfg, badgeList] = await Promise.all([
      api.getRewardSnapshot(),
      api.getRewardsExplainer(),
      api.getRewardConfig(),
      api.listBadges(),
    ]);
    setSnapshot(snap);
    setExplainer(exp);
    setConfig(cfg);
    setBadges(badgeList);
  }, [api]);

  useEffect(() => {
    void load();
  }, [load, version]);

  useEffect(() => {
    if (!badges) return;
    setSelectedBadge((prev) => (prev ? badges.find((b) => b.badgeId === prev.badgeId) ?? null : prev));
  }, [badges]);

  if (!snapshot || !explainer || !config || !badges) {
    return (
      <div style={{ padding: 'var(--space-6) var(--layout-page-margin-phone) 0' }}>
        <SkeletonBlock height={28} width={200} />
        <div style={{ height: 'var(--space-6)' }} />
        <SkeletonBlock height={180} />
        <div style={{ height: 'var(--space-6)' }} />
        <SkeletonBlock height={180} />
      </div>
    );
  }

  const activeConfig = editing && draftConfig ? draftConfig : config;

  function startEditing() {
    setDraftConfig(config);
    setEditing(true);
  }

  async function commitDraft(next: RewardConfig) {
    setDraftConfig(next);
    try {
      await api.updateRewardConfig(next);
    } catch {
      // Invalid intermediate state while editing — swallowed, matches the
      // desktop tree's identical rationale (RewardsScreen.desktop.tsx).
    }
  }

  async function resetToDefaults() {
    await api.resetRewardConfig();
    setResetConfirmOpen(false);
    setEditing(false);
    setDraftConfig(null);
  }

  return (
    <main aria-label="Rewards" style={{ padding: 'var(--space-6) var(--layout-page-margin-phone) var(--space-16)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <Trophy size={16} color="var(--hue-primary-vivid)" aria-hidden="true" />
        <h1 style={{ margin: 0, fontSize: 'var(--type-heading-size)', fontWeight: 'var(--type-heading-weight)', color: 'var(--text-primary)' }}>
          How rewards work
        </h1>
      </header>
      <p style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>
        Every number the engine uses is on this page. No hidden math.
      </p>

      <section style={{ marginTop: 'var(--layout-section-gap)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <SectionHeading>XP sources</SectionHeading>
          <EditValuesToggle
            editing={editing}
            onToggle={() => (editing ? setEditing(false) : startEditing())}
          />
        </div>
        {editing && (
          <p style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>
            Changes recalculate your whole history instantly — past weeks, levels, and badges stay
            consistent. Nothing is lost.
          </p>
        )}
        <div role="list" aria-label="XP sources" style={{ marginTop: 'var(--space-3)' }}>
          {explainer.xpSources.map((row, i) => (
            <XPSourceRow
              key={row.source}
              row={row}
              editing={editing}
              config={activeConfig}
              isLast={i === explainer.xpSources.length - 1}
              onChange={(next) => void commitDraft(next)}
            />
          ))}
        </div>

        <ul style={{ marginTop: 'var(--space-4)', paddingLeft: 'var(--space-5)', color: 'var(--text-secondary)', fontSize: 'var(--type-caption-size)' }}>
          <li>Nothing subtracts. Ever. A miss, a lapse, or an undo never takes XP away — totals just recompute honestly.</li>
          <li>Study minutes are a record, not a payout — the completion tap already paid.</li>
          <li>Accepting a target or date adjustment pays like a completion, not less — recalibrating is the smart play.</li>
        </ul>
      </section>

      <section style={{ marginTop: 'var(--layout-section-gap)' }}>
        <SectionHeading>Level curve</SectionHeading>
        <div role="list" aria-label="Level curve" style={{ marginTop: 'var(--space-3)' }}>
          {explainer.levels.map((row) => (
            <LevelArcRow
              key={row.level}
              row={row}
              snapshot={snapshot}
              editing={editing}
              config={activeConfig}
              onChange={(next) => void commitDraft(next)}
            />
          ))}
        </div>
        <p style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginTop: 'var(--space-3)' }}>
          Past {explainer.levels[explainer.levels.length - 1]?.name ?? 'the top level'}, each level costs the
          same increment again — the curve never runs out.
        </p>
        {editing && (
          <button
            type="button"
            onClick={() => setResetConfirmOpen(true)}
            style={{
              marginTop: 'var(--space-4)',
              color: 'var(--text-secondary)',
              fontSize: 'var(--type-caption-size)',
              fontWeight: 500,
              minHeight: 'var(--touch-target-min)',
            }}
          >
            Reset to defaults
          </button>
        )}
      </section>

      {resetConfirmOpen && (
        <ResetConfirmSheet
          onConfirm={() => void resetToDefaults()}
          onCancel={() => setResetConfirmOpen(false)}
        />
      )}

      <section style={{ marginTop: 'var(--layout-section-gap)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <SectionHeading>Badges</SectionHeading>
          <button
            type="button"
            onClick={() => setCreatingBadge(true)}
            style={{
              color: 'var(--hue-primary)',
              fontSize: 'var(--type-caption-size)',
              fontWeight: 600,
              minHeight: 'var(--touch-target-min)',
            }}
          >
            New badge
          </button>
        </div>
        <div style={{ marginTop: 'var(--space-4)' }}>
          <BadgeListRow badges={badges} onSelect={setSelectedBadge} />
        </div>
      </section>

      <section style={{ marginTop: 'var(--layout-section-gap)' }}>
        <SectionHeading>Theme</SectionHeading>
        <div style={{ marginTop: 'var(--space-3)' }}>
          <ThemeSwitcher />
        </div>
      </section>

      {selectedBadge && (
        <BadgeDetailSheet
          badge={selectedBadge}
          onClose={() => setSelectedBadge(null)}
          onEdit={
            selectedBadge.isStarter
              ? undefined
              : () => {
                  setEditingBadge(selectedBadge);
                  setSelectedBadge(null);
                }
          }
          onArchive={
            selectedBadge.isStarter
              ? undefined
              : () => {
                  void api.archiveBadge(selectedBadge.badgeId).then(() => setSelectedBadge(null));
                }
          }
        />
      )}

      {editingBadge && (
        <BadgeEditorSheet
          initialName={editingBadge.name}
          initialIconId={editingBadge.iconId}
          initialCriterion={editingBadge.criterion}
          onSave={async (draft: BadgeDraft) => {
            await api.editBadge(editingBadge.badgeId, draft);
            setEditingBadge(null);
          }}
          onCancel={() => setEditingBadge(null)}
        />
      )}

      {creatingBadge && (
        <BadgeEditorSheet
          onSave={async (draft: BadgeDraft) => {
            await api.createBadge(draft);
            setCreatingBadge(false);
          }}
          onCancel={() => setCreatingBadge(false)}
        />
      )}
    </main>
  );
}

function SectionHeading({ children }: { readonly children: string }) {
  return (
    <h2
      style={{
        margin: 0,
        fontSize: 'var(--type-heading-size)',
        fontWeight: 'var(--type-heading-weight)',
        color: 'var(--text-primary)',
      }}
    >
      {children}
    </h2>
  );
}

function EditValuesToggle({ editing, onToggle }: { readonly editing: boolean; readonly onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={editing}
      style={{
        color: 'var(--hue-primary)',
        fontSize: 'var(--type-caption-size)',
        fontWeight: 600,
        minHeight: 'var(--touch-target-min)',
      }}
    >
      {editing ? 'Done editing' : 'Edit values'}
    </button>
  );
}

function ResetConfirmSheet({ onConfirm, onCancel }: { readonly onConfirm: () => void; readonly onCancel: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Reset to defaults"
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 90,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'color-mix(in srgb, var(--surface-base) 70%, transparent)',
        padding: 'var(--space-6)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface-overlay)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-2)',
          padding: 'var(--space-6)',
          maxWidth: '360px',
        }}
      >
        <p style={{ margin: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-primary)' }}>
          Reset XP values and the level curve to defaults? Your history recalculates instantly —
          nothing is lost.
        </p>
        <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-5)' }}>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-5)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--hue-primary-vivid)',
              color: 'var(--text-on-accent)',
              fontWeight: 600,
              fontSize: 'var(--type-body-size)',
              border: '1px solid var(--hue-primary-strong)',
            }}
          >
            Reset
          </button>
          <button
            type="button"
            onClick={onCancel}
            style={{
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-5)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-strong)',
              color: 'var(--text-secondary)',
              fontWeight: 500,
              fontSize: 'var(--type-body-size)',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
